'use client'

import { getUser } from '@/actions/user'
import { getCurrencySymbol } from '@/helpers/currency'
import { getUserProfilePictureUrl } from '@/helpers/user'
import dayjs from '@/lib/dayjs'
import { cn } from '@/lib/utils'
import apiFetch from '@/store/helpers/api-fetch'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import useSWR from 'swr'

export default function LiveryOverviewRecentSalesCard({ transaction, index }) {
  const { data: designFiles } = useSWR(
    {
      url: `livery-design/${transaction.liveryDesign.id}/file`,
    },
    apiFetch,
  )

  const [buyer, setBuyer] = useState(null)

  // Call server action to get user details once on mount
  useEffect(() => {
    const callAction = async () => {
      const user = await getUser(transaction.userId)
      setBuyer(user)
    }
    callAction()
  }, [transaction.userId])

  return (
    <div
      key={transaction.id}
      className={cn(
        'relative mt-4 flex items-center',
        index !== 0 && 'border-t border-neutral-blue-700 pt-4',
      )}
    >
      <div className="max-w[35%] desktop-sm:h-22 flex h-auto w-[35%] desktop-sm:w-auto">
        <Link
          href={`/profile-manager/liveries/edit-livery/${transaction.liveryDesign.id}`}
          className="relative aspect-video w-full overflow-hidden rounded pt-[56%]"
        >
          {designFiles ? (
            <Image
              fill
              alt="Event Header Image"
              className="relative overflow-hidden rounded-md object-cover desktop-sm:rounded-sm"
              src={
                designFiles.find(
                  (file) => file.usageHint === 'user-livery-image',
                )?.fileUrl || '/img/default/header.webp'
              }
            />
          ) : (
            <Image
              fill
              alt="Event Header Image"
              className="relative overflow-hidden rounded-md object-cover desktop-sm:rounded-sm"
              src={'/img/default/header.webp'}
            />
          )}
        </Link>
      </div>

      <div className="flex w-full justify-between gap-4">
        {/* name platform game modleName */}
        <div className="col-span-full ml-2 w-full tablet:ml-4 desktop-sm:ml-4">
          <Link
            className="truncate-1 mr-1 break-all text-[16px] font-semibold text-shades-0 tablet:text-[18px] desktop-sm:text-[20px]"
            href={`/profile-manager/liveries/edit-livery/${transaction.liveryDesign.id}`}
          >
            {transaction.liveryDesign.name}
          </Link>
          <div className="font-normal leading-[15px] text-neutral-blue-500 desktop-sm:leading-[16px]">
            <div className="truncate-1 mt-0 flex font-inter text-[14px] text-neutral-blue-500 desktop-sm:text-[14px]">
              {transaction.liveryDesign.platforms?.[0]?.platform?.nicename}
              {transaction.liveryDesign.platforms?.[0]?.platform?.nicename &&
                transaction.liveryDesign.game &&
                ' • '}
              {transaction.liveryDesign.game && (
                <>
                  <span className="hidden tablet:contents">
                    {transaction.liveryDesign.game.longname}
                  </span>
                  <span className="contents tablet:hidden">
                    {transaction.liveryDesign.game.nicename}
                  </span>
                </>
              )}
            </div>
            {transaction.liveryDesign.car?.carClass?.name && (
              <div className="truncate-1 mr-1 mt-[2px] font-inter text-[14px] text-neutral-blue-500 tablet:text-[14px]">
                {transaction.liveryDesign.car.carClass.name}
              </div>
            )}
          </div>
          <div className="mt-0 hidden tablet:flex">
            <div className="items-center align-middle font-bold text-white">
              {transaction?.liveryDesign && (
                <div className="flex gap-[2px]">
                  <div>{getCurrencySymbol(transaction.saleCurrencyId)}</div>
                  <div>{transaction.salePrice}</div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* date designedBy */}
        <div className="flex w-full flex-col justify-between">
          <div className="tablet:inline-block">
            <div className="ml-auto mr-0 w-fit text-[16px] font-semibold text-white">
              {dayjs(transaction.createTs).format('DD/MM/YYYY')}
            </div>
          </div>
          <div className="desktop-sm:inline-block">
            <div className="mt-2 flex items-center align-middle text-[14px] font-semibold text-shades-0 mobile:text-[14px] tablet:text-[16px] desktop-xs:text-[18px]">
              <div className="ml-auto mr-0 flex items-center">
                {buyer && (
                  <div className="relative h-7 w-7 min-w-7 overflow-hidden rounded-full">
                    <Image
                      className="w-7 rounded-full"
                      src={getUserProfilePictureUrl(buyer)}
                      alt="Buyer Logo"
                      fill
                    />
                  </div>
                )}
                <div className="truncate-1 ml-2 break-all font-medium text-white">
                  {buyer?.username ? <>{buyer.username}</> : <>User</>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
